import * as THREE from 'three';

// Terrain type identifiers
export type TerrainType =
  | 'launch_hill'
  | 'mountain_peak'
  | 'mountain_ridge'
  | 'hill'
  | 'crater_bowl';

// Base configuration for all terrain features
export interface TerrainFeatureConfig {
  position: THREE.Vector3;
  rotation?: number; // Y-axis rotation in radians
}

// Launch hill configuration
export interface LaunchHillConfig extends TerrainFeatureConfig {
  peakHeight: number; // Height at the peak (5-15m for Tier 1-2 access)
  length: number; // Length of the hill slope
  width: number; // Width of the hill
  slopeAngle?: number; // Steepness (default calculated from height/length)
}

// Mountain peak configuration
export interface MountainPeakConfig extends TerrainFeatureConfig {
  peakHeight: number; // Height at summit (30-50m for Tier 3-4 access)
  baseRadius: number; // Radius at base
  irregularity?: number; // Amount of noise variation (0-1)
}

// Mountain ridge configuration (doesn't extend base since it uses start/end points)
export interface MountainRidgeConfig {
  startPoint: THREE.Vector3;
  endPoint: THREE.Vector3;
  peakHeight: number; // Height at ridge crest (15-25m)
  width: number; // Width of the ridge
}

// Gentle hill configuration
export interface HillConfig extends TerrainFeatureConfig {
  height: number; // Height at peak (2-6m)
  radius: number; // Falloff radius
}

// Crater bowl configuration
export interface CraterBowlConfig extends TerrainFeatureConfig {
  outerRadius: number; // Radius to rim
  innerRadius: number; // Radius of flat bottom
  rimHeight: number; // Height of rim (5-8m)
  depth: number; // Depth of bowl center
}

// Terrain bounds for spatial grid registration
export interface TerrainBounds {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

// Height sampling result
export interface TerrainHeightResult {
  height: number | null;
  terrainType: TerrainType | null;
  normal?: THREE.Vector3; // Surface normal for slope alignment
}

// Map zones
export const TERRAIN_ZONES = {
  CITY_CORE: 450, // Inner city zone radius
  MIDDLE_ZONE: 600, // Launch hills, crater bowls
  OUTER_ZONE: 900, // Mountain peaks, ridges
} as const;

// Highway tier heights (for reference in terrain design)
export const HIGHWAY_TIERS = {
  TIER_1: 8,
  TIER_2: 18,
  TIER_3: 30,
  TIER_4: 45,
} as const;

// Terrain colors matching synthwave aesthetic
export const TERRAIN_COLORS = {
  BASE: 0x1a1a3e, // Dark metallic base
  BASE_LIGHT: 0x2a2a4e, // Slightly lighter variation
  EDGE_HIGHLIGHT: 0x00ff88, // Neon green edge glow
  PEAK_GLOW: 0xff00ff, // Magenta peak markers
  GRID_LINE: 0x00ffff, // Cyan grid lines
} as const;

// Spatial grid configuration
export const TERRAIN_GRID = {
  CELL_SIZE: 100, // Same as HighwayManager for consistency
  MAP_SIZE: 1800, // Total map extent
} as const;
